import React, { useEffect, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { Mail, Send, Paperclip } from 'lucide-react';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import RefertoPDF from './RefertoPDF';
import FatturaPDF from './FatturaPDF';
import { AllegatoEmail, Clienti, DocumentoCollegato, Storico, StoricoReferti, TipoDocumento } from '../types';

interface EmailComposeModalProps {
  show: boolean;
  onHide: () => void;
  tipo: TipoDocumento;
  storicoId: number;
  etichetta: string; // es. "Fattura n. 12/2026": è l'allegato principale
  cliente: Clienti;
}

const chiave = (doc: AllegatoEmail) => `${doc.tipo}-${doc.id}`;

// Genera il PDF di un documento che non l'ha ancora e lo salva sul server.
// Usa gli stessi componenti dell'anteprima, montati fuori dallo schermo.
const GeneratorePdf: React.FC<AllegatoEmail & { onSalvato: (ok: boolean) => void }> = ({ tipo, id, onSalvato }) => {
  const [dati, setDati] = useState<Storico | StoricoReferti | null>(null);

  useEffect(() => {
    let annullato = false;
    const carica = tipo === 'referto' ? apiService.getReferto(id) : apiService.getFattura(id);
    carica
      .then((risposta) => { if (!annullato) setDati(risposta); })
      .catch((error) => {
        console.error('Errore nel caricamento del documento da generare:', error);
        if (!annullato) onSalvato(false);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, id]);

  if (!dati) return null;
  return (
    <div style={{ position: 'fixed', left: -10000, top: 0, width: 900 }} aria-hidden>
      {tipo === 'referto' ? (
        <RefertoPDF
          referto={(dati as StoricoReferti).referto}
          profile={dati.profile}
          referto_detail={(dati as StoricoReferti).referto_detail}
          clienti={dati.clienti}
          storicoId={id}
          autoSalva
          onSalvato={onSalvato}
        />
      ) : (
        <FatturaPDF
          fattura={(dati as Storico).fattura}
          cliente={dati.clienti}
          profile={dati.profile}
          storicoId={id}
          autoSalva
          onSalvato={onSalvato}
        />
      )}
    </div>
  );
};

const EmailComposeModal: React.FC<EmailComposeModalProps> = ({ show, onHide, tipo, storicoId, etichetta, cliente }) => {
  const { profile } = useAuth();
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [collegati, setCollegati] = useState<DocumentoCollegato[]>([]);
  const [principaleUrl, setPrincipaleUrl] = useState<string | null>(null);
  const [selezionati, setSelezionati] = useState<Set<string>>(new Set());
  const [inGenerazione, setInGenerazione] = useState<AllegatoEmail | null>(null);
  const [sending, setSending] = useState(false);

  const caricaDocumenti = () => apiService.getDocumentiCollegati(tipo, storicoId).then((docs) => {
    setCollegati(docs.collegati);
    setPrincipaleUrl(docs.principale.url);
    return docs;
  });

  // Ad ogni apertura riparte da un'email nuova, con testo di base che il medico può riscrivere
  useEffect(() => {
    if (!show) return;
    const nomeCliente = `${cliente.nome || ''} ${cliente.cognome || ''}`.trim();
    const nomeMedico = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim();
    setTo(cliente.email || '');
    setSubject(etichetta);
    setBody(
      `Gentile ${nomeCliente || 'cliente'},\n\n` +
      `in allegato trova ${tipo === 'fattura' ? 'la fattura' : 'il referto'}.\n\n` +
      `Cordiali saluti,\n${nomeMedico}`
    );
    setCollegati([]);
    setPrincipaleUrl(null);
    setSelezionati(new Set());
    setInGenerazione(null);

    let annullato = false;
    caricaDocumenti()
      .then(() => {
        // Il PDF si rigenera sempre: quello salvato potrebbe essere precedente a una modifica del documento
        if (annullato) return;
        setPrincipaleUrl(null);
        setInGenerazione({ tipo, id: storicoId });
      })
      .catch((error) => console.error('Errore nel caricamento dei documenti collegati:', error));
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tipo, storicoId, etichetta, cliente, profile]);

  const toggleCollegato = (doc: DocumentoCollegato) => {
    const selezionato = selezionati.has(chiave(doc));
    setSelezionati((prev) => {
      const next = new Set(prev);
      if (selezionato) next.delete(chiave(doc)); else next.add(chiave(doc));
      return next;
    });
    // Il PDF di un allegato si rigenera nel momento in cui lo scegli
    if (!selezionato) setInGenerazione({ tipo: doc.tipo, id: doc.id });
  };

  const handleSalvato = (ok: boolean) => {
    const generato = inGenerazione;
    setInGenerazione(null);
    if (ok) {
      caricaDocumenti().catch((error) => console.error('Errore nel ricaricamento dei documenti:', error));
      return;
    }
    toast.error('Non sono riuscito a generare il PDF');
    if (generato && chiave(generato) !== chiave({ tipo, id: storicoId })) {
      setSelezionati((prev) => {
        const next = new Set(prev);
        next.delete(chiave(generato));
        return next;
      });
    }
  };

  const pronto = !sending && !inGenerazione && !!principaleUrl;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await apiService.inviaEmail({
        to: to.trim(),
        subject: subject.trim(),
        body,
        allegati: [
          { tipo, id: storicoId },
          ...collegati.filter((doc) => selezionati.has(chiave(doc))).map(({ tipo, id }) => ({ tipo, id })),
        ],
      });
      toast.success('Email inviata');
      onHide();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Invio dell'email non riuscito");
    } finally {
      setSending(false);
    }
  };

  const stato = (doc: AllegatoEmail, url: string | null) => {
    if (inGenerazione && chiave(inGenerazione) === chiave(doc)) return <small className="text-muted">Preparo il PDF...</small>;
    if (url) return <a href={url} target="_blank" rel="noreferrer">Anteprima</a>;
    return null;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" className="modern-modal">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <Mail size={20} />
          Invia per email
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} className="modern-form">
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Destinatario</Form.Label>
            <Form.Control type="email" value={to} onChange={(e) => setTo(e.target.value)} required />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Oggetto</Form.Label>
            <Form.Control type="text" value={subject} onChange={(e) => setSubject(e.target.value)} required />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Messaggio</Form.Label>
            <Form.Control as="textarea" rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
          </Form.Group>

          <Form.Label className="d-flex align-items-center gap-2">
            <Paperclip size={16} />
            Allegati
          </Form.Label>
          <div className="d-flex align-items-center gap-3">
            <Form.Check type="checkbox" id="allegato-principale" label={`${etichetta} (PDF)`} checked disabled />
            {stato({ tipo, id: storicoId }, principaleUrl)}
          </div>
          {collegati.map((doc) => (
            <div key={chiave(doc)} className="d-flex align-items-center gap-3">
              <Form.Check
                type="checkbox"
                id={`allegato-${chiave(doc)}`}
                label={`${doc.label} (PDF) - ${new Date(doc.data).toLocaleDateString('it-IT')}`}
                checked={selezionati.has(chiave(doc))}
                disabled={!!inGenerazione}
                onChange={() => toggleCollegato(doc)}
              />
              {stato(doc, selezionati.has(chiave(doc)) ? doc.url : null)}
            </div>
          ))}

          {inGenerazione && <GeneratorePdf {...inGenerazione} onSalvato={handleSalvato} />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} className="btn-modern" disabled={sending}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" className="btn-modern d-flex align-items-center gap-2" disabled={!pronto}>
            <Send size={16} />
            {sending ? 'Invio in corso...' : 'Invia'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EmailComposeModal;
